import { ITheme } from 'interfaces/theme';
import merge from 'lodash/merge';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import * as rxjsOperators from 'rxjs-operators';
import { productNewService } from 'services/productNewService';

import apiService, { ApiService } from './api';

export class ThemeService {
  private selectedTheme$ = new BehaviorSubject<ITheme>(null);
  private themes$ = new BehaviorSubject<ITheme[]>(null);
  private updateSubscription: Subscription = null;
  private filterThemes$ = new BehaviorSubject<string>('');
  private isCreatingNewTheme$ = new BehaviorSubject<boolean>(false);

  constructor(private api: ApiService) {}

  public loadThemes(): Observable<ITheme[]> {
    return this.api.get<ITheme[]>('/theme').pipe(
      rxjsOperators.combineLatest(productNewService.getSelectedProduct()),
      rxjsOperators.switchMap(([themes, productSelected]) => {
        const themeActivated = themes.find(theme => theme.linkedProducts.includes(productSelected?.id));

        if (!themeActivated) {
          this.themes$.next(themes);
        }

        if (themeActivated) {
          const index = themes?.indexOf(themeActivated);
          themes.splice(index, 1);

          const newThemes = [themeActivated, ...themes];
          this.themes$.next(newThemes);
        }

        return this.themes$.asObservable();
      }),
      rxjsOperators.take(1)
    );
  }

  public getThemes(): Observable<ITheme[]> {
    return this.themes$.asObservable();
  }

  public getSelectedTheme() {
    return this.selectedTheme$.asObservable();
  }

  public setSelectedTheme(theme: ITheme) {
    const element = theme ? document.getElementById(theme.id) : null;

    if (theme && element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }

    this.selectedTheme$.next(theme);

    this.updateSubscription = this.selectedTheme$
      .asObservable()
      .pipe(
        rxjsOperators.skip(1),
        rxjsOperators.filter(selectedTheme => !!selectedTheme),
        rxjsOperators.debounceTime(100),
        rxjsOperators.distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        rxjsOperators.combineLatest(
          productNewService.getSelectedProduct(),
          this.getThemes().pipe(rxjsOperators.distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
        ),
        rxjsOperators.filter(([, product]) => !!product)
      )
      .subscribe(([newTheme, product, allThemes]) => {
        const themes: Array<ITheme> = JSON.parse(JSON.stringify(allThemes));

        const updatedThemes = JSON.parse(
          JSON.stringify(
            themes.map(t => {
              //To change linked theme
              if (newTheme.linkedProducts.includes(product.id) && t.linkedProducts.includes(product.id)) {
                const index = t.linkedProducts.indexOf(product.id);
                t.linkedProducts.splice(index, 1);
              }
              if (newTheme.id == t.id && newTheme.linkedProducts.toString() !== t.linkedProducts.toString()) {
                const index = t.linkedProducts.indexOf(product.id);
                t.linkedProducts.splice(index, 1);
              }

              //To change Campaigns
              newTheme.campaigns.map(id => {
                if (t.campaigns.includes(id)) {
                  const index = t.campaigns.indexOf(id);
                  t.campaigns.splice(index, 1);
                }
              });

              if (t.id == newTheme.id) {
                t = merge(t, newTheme);
              }
              return t;
            })
          )
        );

        this.themes$.next(updatedThemes);
      });
  }

  public deleteTheme(id: string) {
    return this.api.delete(`/theme/${id}`).pipe(
      rxjsOperators.switchMap(() => this.getThemes().pipe(rxjsOperators.take(1))),
      rxjsOperators.map(themes => {
        return themes.filter(t => t.id != id);
      }),
      rxjsOperators.tap(themes => {
        this.themes$.next(themes);
        themeService.setSelectedTheme(null);
      })
    );
  }

  public duplicateTheme(id: string): any {
    return this.api.get(`/theme/duplicate/${id}`).pipe(
      rxjsOperators.combineLatest(this.getThemes().pipe(rxjsOperators.take(1))),
      rxjsOperators.map(([response, themes]) => [...themes, response]),
      rxjsOperators.tap(themes => this.themes$.next(themes))
    );
  }

  public setLinkedTheme(): Observable<any> {
    let data = null;
    productNewService
      .getSelectedProduct()
      .pipe(
        rxjsOperators.combineLatest(this.getSelectedTheme().pipe(rxjsOperators.take(1))),
        rxjsOperators.take(1),
        rxjsOperators.filter(([product]) => !!product)
      )
      .subscribe(([product, theme]) => {
        data = { themeId: theme.id, productId: product.id };
      });

    return this.api.post('/theme/link', data);
  }

  public setDataTheme(data: ITheme) {
    this.getSelectedTheme()
      .pipe(
        rxjsOperators.take(1),
        rxjsOperators.filter(theme => !!theme)
      )
      .subscribe(theme => {
        let updatedTheme = JSON.parse(JSON.stringify(theme));
        if (data.linkedProducts.length < 1) {
          updatedTheme.linkedProducts = [];
        } else {
          updatedTheme.linkedProducts = data.linkedProducts;
        }
        updatedTheme = merge(updatedTheme, data);

        this.selectedTheme$.next(updatedTheme);
      });
  }

  public addTheme(theme: ITheme) {
    this.getThemes()
      .pipe(
        rxjsOperators.take(1),
        rxjsOperators.filter(theme => !!theme)
      )
      .subscribe(themes => {
        themes.push(theme);
        this.themes$.next(themes);
        this.setSelectedTheme(theme);
      });
  }

  public setFilterThemes(text: string) {
    this.filterThemes$.next(text);
  }

  public getFilterThemes(): Observable<string> {
    return this.filterThemes$.asObservable();
  }

  public getIsCreating(): Observable<boolean> {
    return this.isCreatingNewTheme$.asObservable();
  }

  public setIsCreating(isCreating: boolean) {
    return this.isCreatingNewTheme$.next(isCreating);
  }

  public clearThemes() {
    this.themes$.next(null);
  }
}

export const ThemeServiceFactory = () => new ThemeService(apiService);

export const themeService = ThemeServiceFactory();
